import { useEffect, useMemo, useRef, useState } from "react";
import { getIgoFromUser } from "../../HTTP/user";
import { fetchIgoById } from "../../HTTP/countries";
import { countries } from "../../global/countriesAndSources";
import { translateSearchResults } from "../../HTTP/translation";
import TranslateIcon from "@mui/icons-material/Translate";
import CircularProgress from "@mui/material/CircularProgress";
import { addFavorite, removeFavorite } from "../../HTTP/articles";
import { getSerperActivation, searchWithSerper } from "../../HTTP/search";
import { checkLink, removeLink } from "../../Helper/utils";
import { FactChecking } from "./factChecking";
import "./ForumLatestArticle.css";

const CSE_ID = process.env.REACT_APP_FORUM_ARTICLES_CSE_ID || countries.find((country) => country.code === "wd")?.cse;
const LOG_PREFIX = "[ForumLatestArticle]";
const PAGE_SIZE = 10;

const debugLog = (...args) => console.log(LOG_PREFIX, ...args);

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const normalizeFollowedForums = (response) => {
  const list = Array.isArray(response)
    ? response
    : Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.items)
        ? response.items
        : [];
  return list
    .map((item) => item?.igo || item)
    .filter((forum) => forum?.id || forum?.igo_id || forum?.igoId);
};

const getForumId = (forum) => forum?.id ?? forum?.igo_id ?? forum?.igoId;
const getForumLink = (forum) => forum?.link || forum?.url || forum?.website || "";

const readFavorites = () => {
  const stored = safeJsonParse(localStorage.getItem("favorites"));
  return Array.isArray(stored) ? stored : [];
};

const hasActiveSubscription = () => {
  const subscriptions = safeJsonParse(localStorage.getItem("subscriptions"));
  return subscriptions !== null && (subscriptions.data === undefined || subscriptions.data !== null);
};

const getSearchWindowStart = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return date.toISOString().slice(0, 10);
};

const buildQuery = (forum) => {
  const link = getForumLink(forum);
  if (!link) return "";
  return `${link} after:${getSearchWindowStart()}`;
};

export default function ForumLatestArticle({ initialForum = null }) {
  const user = useMemo(() => safeJsonParse(localStorage.getItem("user")) || {}, []);
  const [forums, setForums] = useState([]);
  const [selectedForumId, setSelectedForumId] = useState("");
  const [isLoadingForums, setIsLoadingForums] = useState(true);
  const [forumError, setForumError] = useState(false);
  const [resultIndex, setResultIndex] = useState(0);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isRetranslating, setIsRetranslating] = useState(false);
  const [translateButtonLabel, setTranslateButtonLabel] = useState("Traduire les résultats");
  const [searchError, setSearchError] = useState(false);
  const [showCseFallback, setShowCseFallback] = useState(false);
  const searchHostRef = useRef(null);
  const cseElementRef = useRef(null);
  const searchObserverRef = useRef(null);
  const searchPollRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const searchRunRef = useRef(0);
  const [serperEnabled, setSerperEnabled] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const serperStateRef = useRef({ page: 0, done: false });
  const useCse = serperEnabled === false && Boolean(CSE_ID);
  const canSearch = serperEnabled === true || useCse;
  const staleLinksRef = useRef(new Set());
  const [favorites, setFavorites] = useState(readFavorites);
  const [isFavoriteBusy, setIsFavoriteBusy] = useState(false);
  const [showFactChecking, setShowFactChecking] = useState(false); // lien de l'article vérifié ou false
  const hasSubscription = useMemo(hasActiveSubscription, []);

  const selectedForum = forums.find((forum) => String(getForumId(forum)) === String(selectedForumId));

  useEffect(() => {
    let active = true;
    let lastLanguage = "";

    const translateButton = async () => {
      const language = (sessionStorage.getItem("currentlanguage") || "fr").split("-")[0];
      if (language === lastLanguage) return;
      lastLanguage = language;
      if (language === "fr") {
        if (active) setTranslateButtonLabel("Traduire les résultats");
        return;
      }

      try {
        const translated = await translateSearchResults("Traduire les résultats");
        if (active && translated) setTranslateButtonLabel(translated);
      } catch (error) {
        if (active) setTranslateButtonLabel("Traduire les résultats");
      }
    };

    translateButton();
    const languageTimer = window.setInterval(translateButton, 800);
    return () => {
      active = false;
      window.clearInterval(languageTimer);
    };
  }, []);

  useEffect(() => {
    let alive = true;
    getSerperActivation()
      .then((enabled) => { if (alive) setSerperEnabled(enabled); })
      .catch((error) => {
        console.error(LOG_PREFIX, "Erreur lors de la lecture de l'option Serper", error);
        if (alive) setSerperEnabled(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    if (initialForum) {
      setForums([initialForum]);
      setSelectedForumId(String(getForumId(initialForum)));
      setIsLoadingForums(false);
      setForumError(false);
      return () => {
        alive = false;
      };
    }

    debugLog("Chargement des forums suivis", { userId: user?.id });
    const loadForums = async () => {
      if (!user?.id) {
        debugLog("Aucun utilisateur trouvé dans localStorage");
        setIsLoadingForums(false);
        return;
      }
      try {
        const response = await getIgoFromUser(user.id);
        const followed = normalizeFollowedForums(response);
        debugLog("Réponse forums suivis", { response, normalized: followed });
        const completeForums = await Promise.all(followed.map(async (forum) => {
          if (getForumLink(forum)) return forum;
          try {
            const completeForum = await fetchIgoById(getForumId(forum));
            debugLog("Forum complété", { forumId: getForumId(forum), completeForum });
            return completeForum;
          } catch (error) {
            console.error(LOG_PREFIX, "Impossible de compléter le forum", { forum, error });
            return forum;
          }
        }));
        if (!alive) return;
        const usableForums = completeForums.filter((forum) => getForumLink(forum));
        debugLog("Forums utilisables", usableForums);
        setForums(usableForums);
      } catch (error) {
        console.error(LOG_PREFIX, "Erreur de chargement des forums", error);
        if (alive) setForumError(true);
      } finally {
        if (alive) setIsLoadingForums(false);
      }
    };
    loadForums();
    return () => {
      alive = false;
    };
  }, [initialForum, user?.id]);

  useEffect(() => {
    debugLog("Initialisation CSE", {
      cseId: CSE_ID,
      selectedForum,
      hasHost: Boolean(searchHostRef.current),
      googleReady: Boolean(window.google?.search?.cse),
    });
    if (!useCse || !selectedForum || !searchHostRef.current) return undefined;

    const host = searchHostRef.current;
    host.replaceChildren();
    const searchElement = document.createElement("div");
    searchElement.className = "gcse-searchresults-only";
    searchElement.dataset.gname = "forum-latest-article-search";
    searchElement.dataset.linktarget = "_blank";
    searchElement.dataset.queryparametername = "q";
    searchElement.dataset.resultsetsize = "20";
    searchElement.dataset.noresultsstring = "Aucun article récent trouvé pour ce forum.";
    host.appendChild(searchElement);

    const executeSearch = () => {
      const element = window.google?.search?.cse?.element?.getElement("forum-latest-article-search");
      if (!element) {
        debugLog("Instance CSE introuvable, nouvelle tentative");
        window.setTimeout(executeSearch, 150);
        return;
      }
      cseElementRef.current = element;
      cseElementRef.current = element;
      debugLog("Instance CSE prête", element);
    };

    const existingScript = document.querySelector(`script[data-forum-cse="${CSE_ID}"]`);
    debugLog("Script CSE", { existing: Boolean(existingScript), src: existingScript?.src });
    if (existingScript && window.google?.search?.cse) {
      executeSearch();
    } else {
      window.__gcse = window.__gcse || {};
      const script = existingScript || document.createElement("script");
      script.src = `https://cse.google.com/cse.js?cx=${CSE_ID}`;
      script.async = true;
      script.dataset.forumCse = CSE_ID;
      script.onload = executeSearch;
      if (!existingScript) document.head.appendChild(script);
    }

    return () => {
      cseElementRef.current = null;
      searchObserverRef.current?.disconnect();
      window.clearInterval(searchPollRef.current);
      window.clearTimeout(searchTimeoutRef.current);
      host.replaceChildren();
    };
  }, [Boolean(selectedForum), useCse]);

  const handleForumChange = (event) => {
    debugLog("Forum sélectionné", { forumId: event.target.value });
    searchRunRef.current += 1;
    searchObserverRef.current?.disconnect();
    window.clearInterval(searchPollRef.current);
    window.clearTimeout(searchTimeoutRef.current);
    setSelectedForumId(event.target.value);
    setResultIndex(0);
    setResults([]);
    setIsSearching(false);
    setIsLoadingMore(false);
    serperStateRef.current = { page: 0, done: false };
    setShowCseFallback(false);
    setSearchError(false);
  };

  const fetchSerperPage = async (page, run) => {
    const response = await searchWithSerper({
      q: `after:${getSearchWindowStart()}`,
      country: "wd",
      links: [getForumLink(selectedForum)],
      page,
      delay: 2,
    });
    if (!Array.isArray(response?.results)) throw new Error("Réponse Serper invalide");
    if (run !== searchRunRef.current) return null;
    return response.results
      .filter((item) => item?.url)
      .map((item) => ({
        title: item.title || item.url,
        link: item.url,
        snippet: item.description || "",
        image: item.image || "",
      }));
  };

  const handleSerperSearch = async () => {
    const run = searchRunRef.current + 1;
    searchRunRef.current = run;
    serperStateRef.current = { page: 0, done: false };
    setIsSearching(true);
    setSearchError(false);
    setResults([]);
    setResultIndex(0);
    try {
      const pageResults = await fetchSerperPage(1, run);
      if (pageResults === null) return;
      serperStateRef.current = { page: 1, done: pageResults.length === 0 };
      debugLog("Résultats Serper enregistrés dans le state", pageResults);
      setResults(pageResults);
    } catch (error) {
      console.error(LOG_PREFIX, "Erreur Serper", error);
      if (run === searchRunRef.current) setSearchError(true);
    } finally {
      if (run === searchRunRef.current) setIsSearching(false);
    }
  };

  // Les résultats déjà en state sont réutilisés ; une nouvelle requête n'est faite qu'une fois épuisés
  const handleNext = async () => {
    if ((resultIndex + 1) * PAGE_SIZE < results.length) {
      setResultIndex((current) => current + 1);
      return;
    }
    if (serperEnabled !== true || serperStateRef.current.done || isLoadingMore) return;
    const run = searchRunRef.current;
    setIsLoadingMore(true);
    try {
      const nextPage = serperStateRef.current.page + 1;
      const pageResults = await fetchSerperPage(nextPage, run);
      if (pageResults === null) return;
      const known = new Set(results.map((result) => result.link));
      const fresh = pageResults.filter((result) => !known.has(result.link));
      serperStateRef.current = { page: nextPage, done: fresh.length === 0 };
      if (fresh.length > 0) {
        setResults([...results, ...fresh]);
        setResultIndex(resultIndex + 1);
      }
    } catch (error) {
      console.error(LOG_PREFIX, "Erreur Serper (page suivante)", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const hasCaptcha = () => {
    const text = searchHostRef.current?.textContent?.toLowerCase() || "";
    return Boolean(searchHostRef.current?.querySelector("iframe[src*='recaptcha'], .g-recaptcha, #recaptcha, [id*='captcha']"))
      || text.includes("captcha")
      || text.includes("je ne suis pas un robot");
  };

  const readResultsFromWidget = () => {
    const resultNodes = searchHostRef.current?.querySelectorAll("div.gsc-result") || [];
    const extractedResults = Array.from(resultNodes).map((node) => {
      const titleLink = node.querySelector("a.gs-title, a.gs-title b");
      const image = node.querySelector(".gs-image-box img");
      return {
        title: titleLink?.textContent?.trim() || "Article sans titre",
        link: titleLink?.href || "#",
        snippet: node.querySelector(".gs-snippet")?.textContent?.trim() || "",
        image: image?.src || "",
      };
    }).filter((result) => result.link !== "#" && result.title !== "Article sans titre");
    debugLog("Résultats lus dans le DOM CSE", {
      nodeCount: resultNodes.length,
      resultCount: extractedResults.length,
      results: extractedResults,
      hostHtml: searchHostRef.current?.innerHTML?.slice(0, 1000),
    });
    return extractedResults;
  };

  const handleSearch = () => {
    if (serperEnabled === true) {
      handleSerperSearch();
      return;
    }
    const element = cseElementRef.current;
    const searchRun = searchRunRef.current + 1;
    searchRunRef.current = searchRun;
    staleLinksRef.current = new Set(readResultsFromWidget().map((result) => result.link));
    debugLog("Demande de recherche", {
      elementReady: Boolean(element),
      selectedForum,
      query: selectedForum ? buildQuery(selectedForum) : "",
    });
    if (!element || !selectedForum) {
      console.error(LOG_PREFIX, "Recherche impossible: widget ou forum absent");
      setSearchError(true);
      return;
    }

    searchObserverRef.current?.disconnect();
    window.clearInterval(searchPollRef.current);
    window.clearTimeout(searchTimeoutRef.current);
    setIsSearching(true);
    setSearchError(false);
    setResults([]);
    setResultIndex(0);
    setShowCseFallback(false);

    if (typeof element.clearAllResults === "function") {
      element.clearAllResults();
    }

    let paginationStarted = false;
    const collectResults = () => {
      if (searchRun !== searchRunRef.current || paginationStarted) return;
      const nextResults = readResultsFromWidget().filter((result) => !staleLinksRef.current.has(result.link));
      if (nextResults.length > 0) {
        paginationStarted = true;
        debugLog("Résultats enregistrés dans le state", nextResults);
        setResults(nextResults);
        setIsSearching(false);
        searchObserverRef.current?.disconnect();
        window.clearInterval(searchPollRef.current);
        loadMorePages(searchRun, nextResults);
      }
    };

    // Google CSE ne renvoie que ~4 résultats par page : on parcourt les pages suivantes
    const loadMorePages = async (run, firstResults) => {
      const MAX_PAGES = 5;
      const collected = new Map(firstResults.map((result) => [result.link, result]));
      for (let page = 2; page <= MAX_PAGES; page += 1) {
        const pageButton = Array.from(searchHostRef.current?.querySelectorAll(".gsc-cursor-page") || [])
          .find((node) => node.textContent.trim() === String(page));
        if (!pageButton) break;
        const previousFirstLink = readResultsFromWidget()[0]?.link;
        pageButton.click();
        let changed = false;
        for (let attempt = 0; attempt < 20; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, 250));
          if (run !== searchRunRef.current) return;
          const current = readResultsFromWidget();
          if (current.length > 0 && current[0].link !== previousFirstLink) {
            changed = true;
            current.filter((result) => !staleLinksRef.current.has(result.link))
              .forEach((result) => collected.set(result.link, result));
            break;
          }
        }
        if (!changed) break;
        setResults(Array.from(collected.values()));
      }
    };

    searchObserverRef.current = new MutationObserver(collectResults);
    searchObserverRef.current.observe(searchHostRef.current, { childList: true, subtree: true });
    const query = buildQuery(selectedForum);
    debugLog("Exécution de la requête CSE", query);
    element.execute(query);
    searchPollRef.current = window.setInterval(collectResults, 250);
    searchTimeoutRef.current = window.setTimeout(() => {
      if (searchRun !== searchRunRef.current) return;
      const nextResults = readResultsFromWidget().filter((result) => !staleLinksRef.current.has(result.link));
      if (nextResults.length === 0) {
        if (hasCaptcha()) {
          debugLog("Captcha détecté, affichage du widget Google", { query });
          setShowCseFallback(true);
        } else {
          debugLog("Aucun résultat trouvé après 5 secondes", { query });
        }
      }
      collectResults();
      window.clearInterval(searchPollRef.current);
      setIsSearching(false);
    }, 5000);
  };

  const handleRetranslate = () => {
    const select = document.querySelector(".goog-te-combo");
    if (!select) return;

    const targetLanguage = (sessionStorage.getItem("currentlanguage")
      || document.documentElement.lang
      || "fr").split("-")[0];
    const temporaryLanguage = targetLanguage === "fr" ? "en" : "fr";

    setIsRetranslating(true);
    window.__gt_retranslate_lock = true;
    select.value = temporaryLanguage;
    select.dispatchEvent(new Event("change"));

    window.setTimeout(() => {
      select.value = targetLanguage;
      select.dispatchEvent(new Event("change"));
      window.setTimeout(() => {
        const result = searchHostRef.current?.querySelector("div.gsc-result");
        const hasCompleteResult = Boolean(
          result?.querySelector("a.gs-title")
          && result?.querySelector(".gs-snippet")
        );
        if (!hasCompleteResult && cseElementRef.current && selectedForum) {
          cseElementRef.current.execute(buildQuery(selectedForum));
        }
        window.__gt_retranslate_lock = false;
        setIsRetranslating(false);
      }, 1200);
    }, 800);
  };

  const pageStart = resultIndex * PAGE_SIZE;
  const pageResults = results.slice(pageStart, pageStart + PAGE_SIZE);

  const handleToggleFavorite = async (currentResult) => {
    if (!currentResult || isFavoriteBusy) return;
    const isCurrentFavorite = checkLink(favorites, currentResult.link) === true;
    setIsFavoriteBusy(true);
    try {
      if (isCurrentFavorite) {
        const removed = await removeFavorite({ link: currentResult.link });
        if (removed) {
          const updated = removeLink(favorites, currentResult.link);
          localStorage.setItem("favorites", JSON.stringify(updated));
          setFavorites(updated);
        }
        return;
      }
      const favorite = {
        title: currentResult.title,
        link: currentResult.link,
        image: currentResult.image || "",
        country: selectedForum?.country_name || selectedForum?.country || "World",
        publication_date: new Date().toISOString().replace("T", " ").slice(0, 19),
        description: currentResult.snippet || "",
        igo_id: selectedForum ? getForumId(selectedForum) : selectedForumId,
      };
      const res = await addFavorite(favorite);
      if (res?.ok === true) {
        const updated = [{ ...favorite, id: res.id }, ...favorites];
        localStorage.setItem("favorites", JSON.stringify(updated));
        setFavorites(updated);
      }
    } catch (error) {
      console.error(LOG_PREFIX, "Erreur lors de la mise à jour du favori", error);
    } finally {
      setIsFavoriteBusy(false);
    }
  };

  const canGoBack = resultIndex > 0;
  const hasNextPage = (resultIndex + 1) * PAGE_SIZE < results.length
    || (serperEnabled === true && results.length > 0 && !serperStateRef.current.done);

  return (
    <section className="home-hub-forum-article" aria-label={initialForum ? `Derniers articles de ${initialForum.label || initialForum.name}` : "Dernier article d'un forum suivi"}>
      <div className="home-hub-forum-article-header">
        <div>
          <p className="home-hub-eyebrow">{initialForum ? "Forum global sélectionné" : "Forums suivis"}</p>
          <h2>{initialForum ? `Derniers articles — ${initialForum.label || initialForum.name}` : "Dernier article publié"}</h2>
        </div>
        {selectedForum && <span className="home-hub-forum-article-window">Depuis le {getSearchWindowStart()}</span>}
      </div>

      {!initialForum && <label className="home-hub-forum-select-label" htmlFor="home-hub-forum-select">Choisir un forum global</label>}
      <div className="home-hub-forum-search-row">
        {!initialForum && (
          <select id="home-hub-forum-select" className="home-hub-forum-select" value={selectedForumId} onChange={handleForumChange} disabled={isLoadingForums || forums.length === 0}>
            <option value="">{isLoadingForums ? "Chargement des forums suivis..." : "Sélectionner un forum"}</option>
            {forums.map((forum) => <option key={getForumId(forum)} value={getForumId(forum)}>{forum.label || forum.name || "Forum global"}</option>)}
          </select>
        )}

        {selectedForum && canSearch && (
          <button type="button" className="home-hub-forum-search-button" onClick={handleSearch} disabled={isSearching}>
            {isSearching ? "Recherche en cours..." : initialForum ? "Chercher les derniers articles" : "Rechercher les articles"}
          </button>
        )}
      </div>

      {selectedForum && getForumLink(selectedForum) && (
        <a className="home-hub-forum-link" href={getForumLink(selectedForum)} target="_blank" rel="noopener noreferrer">
          Ouvrir le site du forum
        </a>
      )}
      {selectedForum && canSearch && (
        <button
          type="button"
          className="home-hub-forum-translate-button"
          onClick={handleRetranslate}
          disabled={isRetranslating}
          aria-label={isRetranslating ? "Traduction en cours" : translateButtonLabel}
          title={isRetranslating ? "Traduction en cours" : translateButtonLabel}
        >
          {isRetranslating ? <CircularProgress size={18} color="inherit" /> : <TranslateIcon fontSize="small" />}
        </button>
      )}

      {forumError && <p className="home-hub-activity-state home-hub-activity-error">La liste des forums suivis n'a pas pu être chargée.</p>}
      {!isLoadingForums && !forumError && forums.length === 0 && <p className="home-hub-activity-state">Suivez un forum global pour voir ses derniers articles ici.</p>}
      {selectedForum && serperEnabled === false && !CSE_ID && <p className="home-hub-activity-state home-hub-activity-error">La recherche Google CSE n'est pas configurée.</p>}
      {selectedForum && canSearch && (
        <>
          {isSearching && <p className="home-hub-forum-search-loading">Recherche des articles en cours...</p>}
          <div ref={searchHostRef} className={`home-hub-forum-search${showCseFallback ? " home-hub-forum-search-visible" : ""}`} />
          {searchError && <p className="home-hub-activity-state home-hub-activity-error">La recherche n'a pas pu être chargée.</p>}
          {!isSearching && results.length === 0 && <p className="home-hub-activity-state">Lancez une recherche pour afficher les articles récents.</p>}
          {pageResults.map((result) => {
            const isFavorite = checkLink(favorites, result.link) === true;
            return (
              <article className="home-hub-forum-result" key={result.link}>
                {result.image && <img src={result.image} alt="" />}
                <div>
                  <h3>{result.title}</h3>
                  {result.snippet && <p>{result.snippet}</p>}
                  <a href={result.link} target="_blank" rel="noopener noreferrer">Lire l'article</a>
                </div>
                <div className="home-hub-forum-result-actions">
                  <button
                    type="button"
                    className="home-hub-forum-bookmark"
                    onClick={() => handleToggleFavorite(result)}
                    disabled={isFavoriteBusy}
                    title={isFavorite ? "Retirer le favori" : "Ajouter aux favoris"}
                    aria-label={isFavorite ? "Retirer le favori" : "Ajouter aux favoris"}
                    aria-pressed={isFavorite}
                  >
                    <img src={`${process.env.PUBLIC_URL}/${isFavorite ? "bookmark.svg" : "bookmarklight.svg"}`} alt="" />
                  </button>
                  {hasSubscription && (
                    <button type="button" className="home-hub-forum-factcheck" onClick={() => setShowFactChecking(result.link)} title="Vérifier cet article">
                      Vérificateur des faits
                    </button>
                  )}
                </div>
              </article>
            );
          })}
          {showFactChecking && (
            <FactChecking articleLink={showFactChecking} setShowPopUpFactChecking={setShowFactChecking} />
          )}
          <div className="home-hub-forum-pagination" aria-label="Navigation des articles">
            <button type="button" onClick={() => setResultIndex((current) => current - 1)} disabled={!canGoBack} aria-label="Page précédente">←</button>
            <span>{results.length ? `Articles ${pageStart + 1}-${pageStart + pageResults.length} sur ${results.length}` : "Aucun article"}</span>
            <button type="button" onClick={handleNext} disabled={!hasNextPage || isLoadingMore} aria-label="Page suivante">→</button>
          </div>
        </>
      )}
    </section>
  );
}
